/**
 * feature/catalogue — spec §F8 (Data Catalogue).
 *
 * One `/datasets` fetch (Task 6's `fetchDatasets`), filtered client-side by the pure
 * helpers in `catalogue.ts` (`applyFilters`/`parseCatalogueSearch`/`serialiseCatalogueSearch`
 * — the URL is the source of truth for the four facets and the search text, mirroring
 * `Topbar`'s own `useSearchParams` usage). The rail's per-option counts come straight off
 * the envelope's own `facets` block: it is already computed over the whole catalogue
 * (`DatasetsResponse.facets`'s own doc comment), which is exactly what a facet count next
 * to a checkbox needs — recomputing it client-side with `catalogue.ts`'s `facetCounts`
 * would just repeat what the server already answered.
 *
 * The record drawer is `DatasetRecord.tsx`, which this page no longer renders: since client
 * item 6 a card click only writes the dataset id to the store (`openRecord`) and the
 * app-level `app/RecordHost.tsx` draws it, so the same record opens identically from the
 * map's layer list.
 *
 * **Client, 2026-09-16** — two changes, one request each:
 *
 * - *"check also by Data Source — MEL, Baseline Upload, ISRIC etc."* — a fourth facet group
 *   whose options are the server's `facets.source` (free-text `source_org`, so `label` is
 *   the id and there is nothing to translate). **This one narrows on the server**: ticking
 *   an organisation re-keys the query to `['datasets', lang, source]` and re-reads
 *   `/datasets?source=…`, so the wire carries the filter rather than the client throwing
 *   rows away. `?source=` takes one organisation, so a reader who ticks a second falls back
 *   to the whole-catalogue read and `applyFilters` ORs them client-side — the rail is a
 *   multi-select and staying one is worth one unfiltered fetch in the uncommon case. Either
 *   way `facets` describes the whole catalogue, so the counts beside the checkboxes never
 *   move.
 * - *"make the Data catalogue highly professional, it looks cluttered"* — the mockup's
 *   `.cat-toolbar` becomes one header row (title · count · sort · search), the blue schema
 *   note leaves the rail for a hairline footer under the grid, and the card drops its three
 *   coloured pills and its `— · 2026-09-16` footer for a single "Source · Format · Updated"
 *   line. The Sort select is client-side (`sortRows`) and deliberately *not* in the URL:
 *   `?q=&domain=&type=&cls=&src=` is what a shared catalogue link is about, and a reading
 *   order is not part of what one reader is showing another.
 *
 * **Client, 2026-09-16 (round 2)** — *"still not happy with our modernity and creativity on
 * the catalogue"*. The header row becomes a band (`CatalogueHero`): title, what the
 * catalogue holds by data type, the search centred at 48 px, and a "Browse by" row of quick
 * chips that toggle the *same* `CatalogueFilters` the rail does — one state, two ways in, so
 * a chip and its checkbox can never disagree. What is left on the page's own header row is
 * the three things that are about *this* reading of the catalogue rather than about the
 * catalogue: the count, the sort, and a grid/list switch.
 *
 * The list is `DatasetListRow` — the same rows, the same handlers and the same
 * `card-preview-…` test ids, in 56 px columns, for a reader comparing rather than browsing.
 * The choice is a preference (`sv.catView` in `localStorage`), not view state: it is about
 * how this reader reads, so it stays off the permalink for the reason the sort does.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { fetchDatasets, queryKeys, type DatasetRow } from '../../lib/api';
import { Icon } from '../../lib/icons';
import { useAppStore } from '../../lib/state';
import { Chip } from '../../components/Chip';
import { Seg } from '../../components/Seg';
import {
  applyFilters,
  parseCatalogueSearch,
  serialiseCatalogueSearch,
  domainFacetsById,
  formatUpdated,
  readCatView,
  sortRows,
  typeCounts,
  visibleSourceFacets,
  writeCatView,
  DEFAULT_SORT,
  type CatalogueFilters,
  type CatalogueSort,
  type CatalogueView,
  type QuickChip,
} from './catalogue';
import { CatalogueHero } from './CatalogueHero';
import { DatasetListRow } from './DatasetListRow';
import { DatasetThumb, DatasetTypeBadge } from './DatasetThumb';
import { useDatasetPreview } from './preview';

/** §F8 toolbar order — fixed, not the payload's own array order, so the rail never reflows. */
const TYPE_ORDER: DatasetRow['data_type'][] = ['raster', 'vector', 'tabular'];
const CLASSIFICATION_ORDER: DatasetRow['classification'][] = ['open', 'sensitive', 'restricted'];

const SORTS: CatalogueSort[] = ['updated', 'title'];

const VIEWS: CatalogueView[] = ['grid', 'list'];

/**
 * A glyph per rail group (client, 2026-09-16 round 2). Four identical 12 px heads read as
 * one undifferentiated column; a layers/table/lock/building set lets the eye land on the
 * right group without reading all four labels. They are the same icons the chips and the
 * cards use for the same ideas — a lock is "classification" everywhere in this product.
 */
const FACET_ICONS = {
  domain: 'layers',
  type: 'table',
  cls: 'lock',
  src: 'building',
} as const;

/** The four rail groups. `src` is the client, 2026-09-16 addition. */
type FacetGroup = 'domain' | 'type' | 'cls' | 'src';

export function CataloguePage() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [facetsOpen, setFacetsOpen] = useState(false);
  const [sort, setSort] = useState<CatalogueSort>(DEFAULT_SORT);
  /** Is the source group showing its whole long tail? See `visibleSourceFacets`. */
  const [allSources, setAllSources] = useState(false);
  /**
   * Grid or list. Read from `localStorage` once, on mount — a lazy initialiser rather than
   * an effect, so the first paint is already the layout the reader chose and the page never
   * flashes the grid on its way to the list.
   */
  const [view, setView] = useState<CatalogueView>(readCatView);
  // Client item 6: the record is app state and `app/RecordHost.tsx` draws it, so the page
  // only ever names the dataset it wants opened.
  const openRecord = useAppStore((s) => s.openRecord);

  /**
   * "Preview on map" from the card itself (client, 2026-09-15), the same gesture the record
   * modal offers — and since client item 9 the *same handler*, so the two controls cannot
   * disagree about which layer a card is about. See `preview.ts` for the rule.
   */
  const previewOnMap = useDatasetPreview();

  const filters = useMemo(() => parseCatalogueSearch(searchParams.toString()), [searchParams]);

  /**
   * `?record=<id>` — the deep link a harvested record's `dcat:landingPage` points at
   * (design 2026-09-21). The record drawer is store state, not a route, so a harvester
   * following a link into the catalogue would otherwise land on the grid with nothing
   * open. The param is read once and then dropped from the URL: it is an *instruction*,
   * not a filter, and leaving it there would reopen the record every time the reader
   * closed it — and would ride along into whatever link they copied next.
   */
  const deepLinked = useRef(false);
  useEffect(() => {
    if (deepLinked.current) return;
    const id = searchParams.get('record');
    if (!id) return;
    deepLinked.current = true;
    openRecord(id);
    const next = new URLSearchParams(searchParams);
    next.delete('record');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, openRecord]);

  /**
   * The organisation the *server* narrows to, or `null` for the whole catalogue: `?source=`
   * is exact and single-valued, so one ticked source goes on the wire and two or more come
   * back client-side (see this module's header).
   */
  const serverSource = filters.src.size === 1 ? [...filters.src][0]! : null;

  /**
   * `keepPreviousData` is what makes a server-side facet feel like a client-side one: without
   * it, ticking an organisation empties `data` for the length of a round trip, so the grid
   * blanks, the count drops to `0 of 0`, the rail loses every option *and its own ticked
   * checkbox*, and "No datasets match the current filters." flashes under it. With it the
   * previous answer stays on screen — greyed, via `.cat-main.loading` — until the narrowed
   * one lands.
   */
  const { data, isPending, isPlaceholderData } = useQuery({
    queryKey: queryKeys.datasets(undefined, serverSource),
    queryFn: fetchDatasets,
    placeholderData: keepPreviousData,
  });
  /** Is what is on screen the *previous* read, still waiting on this one? */
  const stale = isPending || isPlaceholderData;
  const rows = useMemo(() => data?.results ?? [], [data]);
  const total = data?.total ?? rows.length;

  const filtered = useMemo(() => applyFilters(rows, filters), [rows, filters]);
  const shown = useMemo(
    () => sortRows(filtered, sort, i18n.language),
    [filtered, sort, i18n.language],
  );

  const writeFilters = (next: CatalogueFilters) => {
    setSearchParams(serialiseCatalogueSearch(next).replace(/^\?/, ''), { replace: true });
  };

  const setQuery = (q: string) => writeFilters({ ...filters, q });

  const toggleFacet = (group: FacetGroup, value: string) => {
    const set = new Set(filters[group]);
    if (set.has(value)) set.delete(value);
    else set.add(value);
    writeFilters({ ...filters, [group]: set });
  };

  /** A chip is ticked when its own facet value is — the rail's state, read from one place. */
  const chipOn = (chip: QuickChip) => filters[chip.group].has(chip.value);
  const toggleChip = (chip: QuickChip) => toggleFacet(chip.group, chip.value);

  const chooseView = (next: CatalogueView) => {
    setView(next);
    writeCatView(next);
  };

  const activeFilterCount =
    filters.domain.size + filters.type.size + filters.cls.size + filters.src.size;

  const domainFacets = data?.facets.domain ?? [];
  const counts = typeCounts(data?.facets.data_type);
  const clsCounts = new Map((data?.facets.classification ?? []).map((f) => [f.id, f.count]));
  const sourceFacets = data?.facets.source ?? [];
  const shownSources = allSources
    ? sourceFacets
    : visibleSourceFacets(sourceFacets, filters.src);
  const domainById = domainFacetsById(domainFacets);

  const cardKeyDown = (id: string) => (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openRecord(id);
    }
  };

  /** One group head: its glyph, then its name. The accent bar is `.facet h4::before`. */
  const facetHead = (group: FacetGroup, label: string) => (
    <h4>
      <Icon name={FACET_ICONS[group]} size={13} />
      <span>{label}</span>
    </h4>
  );

  /** One checkbox row: the same shape in all four groups, so the rail reads as one column. */
  const facetRow = (
    group: FacetGroup,
    value: string,
    label: string,
    count: number,
  ) => (
    <label key={value}>
      <input
        type="checkbox"
        checked={filters[group].has(value)}
        onChange={() => toggleFacet(group, value)}
      />
      <span className="fname">{label}</span>
      <span className="fcount">{count}</span>
    </label>
  );

  return (
    <div className="view cat-view" data-testid="catalogue-page">
      {/* Full-bleed, so it sits outside `.cat-wrap`'s 1280 px rail-and-grid layout — a band
          that stopped at the content column would be a wide card, not a header. */}
      <CatalogueHero
        facets={data?.facets}
        total={total}
        q={filters.q}
        onQuery={setQuery}
        isOn={chipOn}
        onChip={toggleChip}
      />

      <div className="cat-wrap">
        <aside className={`facets${facetsOpen ? ' open' : ''}`} data-testid="catalogue-facets">
          <button
            type="button"
            className="btn ghost facet-toggle"
            data-testid="facet-toggle"
            aria-expanded={facetsOpen}
            onClick={() => setFacetsOpen((v) => !v)}
          >
            {t('catalogue.filters', { n: activeFilterCount })}
          </button>

          <div className="facet" data-testid="facet-domain">
            {facetHead('domain', t('catalogue.facets.domain'))}
            {domainFacets.map((d) => facetRow('domain', d.id, d.name, d.count))}
          </div>

          <div className="facet" data-testid="facet-type">
            {facetHead('type', t('catalogue.facets.type'))}
            {TYPE_ORDER.map((id) =>
              facetRow('type', id, t(`catalogue.type.${id}`), counts[id]),
            )}
          </div>

          <div className="facet" data-testid="facet-classification">
            {facetHead('cls', t('catalogue.facets.classification'))}
            {CLASSIFICATION_ORDER.map((id) =>
              facetRow('cls', id, t(`catalogue.cls.${id}`), clsCounts.get(id) ?? 0),
            )}
          </div>

          {/* Client, 2026-09-16. The options are the server's own distinct `source_org`
              values: an organisation is not a translated concept, so `label === id` in
              both languages. The catalogue has 27 of them, so the group opens on the
              eight largest (plus anything ticked) and says how many it is hiding. */}
          <div className="facet" data-testid="facet-source">
            {facetHead('src', t('catalogue.facets.source'))}
            {shownSources.map((f) => facetRow('src', f.id, f.label ?? f.id, f.count))}
            {sourceFacets.length > shownSources.length || allSources ? (
              <button
                type="button"
                className="facet-more"
                data-testid="facet-source-toggle"
                aria-expanded={allSources}
                onClick={() => setAllSources((v) => !v)}
              >
                {allSources
                  ? t('catalogue.facets.showFewer')
                  : t('catalogue.facets.showAll', { n: sourceFacets.length })}
              </button>
            ) : null}
          </div>
        </aside>

        <div className={`cat-main${stale ? ' loading' : ''}`} aria-busy={stale || undefined}>
          {/* The title and the search moved into the band above; what is left is the three
              controls that describe *this* reading of the catalogue. */}
          <header className="cat-head">
            <p className="cat-count">
              <span data-testid="catalogue-count">
                {t('catalogue.ofTotal', { n: shown.length, total })}
              </span>{' '}
              {t('catalogue.countUnit')}
            </p>
            <label className="cat-sort">
              <span>{t('catalogue.sort.label')}</span>
              <select
                className="std"
                data-testid="catalogue-sort"
                value={sort}
                onChange={(e) => setSort(e.target.value as CatalogueSort)}
              >
                {SORTS.map((id) => (
                  <option key={id} value={id}>
                    {t(`catalogue.sort.${id}`)}
                  </option>
                ))}
              </select>
            </label>
            <Seg
              className="seg cat-viewseg"
              ariaLabel={t('catalogue.view.label')}
              value={view}
              onChange={chooseView}
              options={VIEWS.map((id) => ({
                value: id,
                label: t(`catalogue.view.${id}`),
                testId: `cat-view-${id}`,
              }))}
            />
          </header>

          {/* One container, one test id, two layouts: the grid and the list are the same
              answer drawn two ways, and a reader switching between them has not navigated
              anywhere. Only the class (and what it holds) changes. */}
          <div
            className={view === 'list' ? 'cat-list' : 'cat-grid'}
            data-testid="catalogue-grid"
            data-view={view}
          >
            {shown.length === 0 ? (
              // Never while a read is in flight: an empty grid then means "not answered
              // yet", and saying "nothing matches" would be a claim about an answer the
              // page does not have. The first load shows nothing at all; a re-read keeps
              // the previous rows, so this only ever prints a real empty result.
              stale ? null : <div className="note info">{t('catalogue.noMatch')}</div>
            ) : view === 'list' ? (
              shown.map((row) => (
                <DatasetListRow
                  key={row.id}
                  row={row}
                  domain={row.domain ? domainById.get(row.domain) : undefined}
                  onOpen={openRecord}
                  onPreview={previewOnMap}
                />
              ))
            ) : (
              shown.map((row) => {
                const domainInfo = row.domain ? domainById.get(row.domain) : undefined;
                return (
                  <article
                    key={row.id}
                    className="ds-card"
                    data-testid={`ds-card-${row.id}`}
                    role="button"
                    tabIndex={0}
                    // The card's 3 px left edge is its domain (`.ds-card::before` reads the
                    // custom property). A row with no domain gets no edge at all rather than
                    // a grey one: a colour that means "nothing" is still a colour to decode.
                    style={
                      domainInfo ? ({ '--ds-domain': domainInfo.color } as CSSProperties) : undefined
                    }
                    onClick={() => openRecord(row.id)}
                    onKeyDown={cardKeyDown(row.id)}
                  >
                    <div className="ds-pic">
                      <DatasetThumb row={row} domain={domainInfo} />
                      {row.is_provisional && (
                        <span className="ds-badge ds-badge-l">
                          <Chip kind="provisional" title={t('catalogue.provisionalTitle')}>
                            {t('catalogue.provisional')}
                          </Chip>
                        </span>
                      )}
                      <span className="ds-badge ds-badge-r">
                        <Chip kind={row.classification} />
                      </span>
                      {/* The lock was the fourth item on a one-line meta row, where
                          `overflow:hidden` would have clipped it on a narrow card. It
                          belongs on the picture anyway: it says what the reader may do
                          with this dataset, which is what the classification chip above
                          it says too. */}
                      {/* What shape this dataset is, answered before a word is read. */}
                      <DatasetTypeBadge row={row} domain={domainInfo} />
                      {row.access === 'none' && (
                        <span className="ds-badge ds-badge-lock">
                          <Icon name="lock" size={11} /> {t('catalogue.noAccess')}
                        </span>
                      )}
                    </div>

                    <h4 className="ds-name">{row.title}</h4>
                    <p className="abstract">{row.abstract}</p>

                    {/* One line, not three chips: the card's identity is who made it, in
                        what shape, and how current it is. Exactly one of the three may be
                        shortened, and it is the source — the only one of them the rail
                        beside the grid also names, so a reader can always read in full
                        somewhere what a card abbreviates. The format and the date are
                        short, fixed and never cut. */}
                    <p className="ds-meta">
                      <span className="ds-meta-src">{row.source_org}</span>
                      <span className="ds-meta-fmt">{row.format}</span>
                      <span className="ds-meta-upd">
                        {t('catalogue.card.updated', {
                          date: formatUpdated(row.updated_at, i18n.language),
                        })}
                      </span>
                    </p>

                    <div className="ds-foot">
                      <button
                        type="button"
                        className="btn ghost sm"
                        data-testid={`card-preview-${row.id}`}
                        // §F9: the server says whether this row has anywhere to be
                        // previewed — the eleven layerless MELS/baseline rows do, and a
                        // table-only record does not.
                        disabled={row.preview === null}
                        title={row.preview === null ? t('catalogue.record.tabularPreview') : undefined}
                        onClick={(e) => {
                          e.stopPropagation();
                          previewOnMap(row);
                        }}
                      >
                        <Icon name="map" size={13} /> {t('catalogue.record.preview')}
                      </button>
                      <button
                        type="button"
                        className="btn ghost sm"
                        data-testid={`card-details-${row.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          openRecord(row.id);
                        }}
                      >
                        {t('catalogue.card.details')}
                      </button>
                    </div>
                  </article>
                );
              })
            )}
          </div>

          {/* Was a blue `.note info` box at the foot of the rail, where it competed with the
              facets for the reader's first look. It is a footnote, so it reads as one. */}
          <p className="cat-note">{t('catalogue.metaNote')}</p>
        </div>
      </div>
    </div>
  );
}

export default CataloguePage;
